/**
 * SuppliersDedup — Sprint 88 : détection auto de doublons fournisseurs,
 * fusion avec preview + rollback 30j, bulk rename.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Users, AlertTriangle, RotateCcw, Merge, CheckCircle2, Pencil, ChevronDown, ChevronRight, History, Loader2 } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  exact_siret: { label: "SIRET identique", color: "bg-red-100 text-red-700" },
  exact_name: { label: "Nom identique", color: "bg-orange-100 text-orange-700" },
  fuzzy: { label: "Nom similaire", color: "bg-amber-100 text-amber-800" },
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
}) : "—";

export default function SuppliersDedup() {
  const [tab, setTab] = useState("duplicates");
  const [fuzzy, setFuzzy] = useState(85);
  const [loading, setLoading] = useState(false);
  const [scan, setScan] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pendingMerge, setPendingMerge] = useState(null);
  const [merging, setMerging] = useState(false);
  const [ops, setOps] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkIds, setBulkIds] = useState([]);
  const [bulkNewName, setBulkNewName] = useState("");
  const [bulkNewSiret, setBulkNewSiret] = useState("");

  const loadDuplicates = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/suppliers/duplicates?fuzzy=${fuzzy}`);
      setScan(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const loadOps = async () => {
    try {
      const r = await api.get("/suppliers/dedup/operations");
      setOps(r.data.operations || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { loadDuplicates(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (tab === "history") loadOps(); }, [tab]);

  const openPreview = async (group, targetId, selectedSourceIds) => {
    try {
      const r = await api.post("/suppliers/dedup/preview", {
        target_id: targetId,
        source_ids: selectedSourceIds,
      });
      setPreview(r.data);
      setPendingMerge({ target_id: targetId, source_ids: selectedSourceIds, group });
      setPreviewOpen(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const executeMerge = async () => {
    if (!pendingMerge) return;
    setMerging(true);
    try {
      const r = await api.post("/suppliers/dedup/merge", {
        target_id: pendingMerge.target_id,
        source_ids: pendingMerge.source_ids,
      });
      toast.success(
        <div>
          <p className="font-medium">Fusion effectuée</p>
          <p className="text-xs">{r.data.sources_merged} fournisseur(s), {r.data.invoices_reassigned} facture(s) réassignée(s)</p>
        </div>,
        {
          action: {
            label: "Annuler",
            onClick: async () => {
              try {
                await api.post(`/suppliers/dedup/rollback/${r.data.op_id}`);
                toast.success("Fusion annulée");
                await loadDuplicates();
              } catch (e) { toast.error(apiError(e)); }
            }
          },
          duration: 8000,
        }
      );
      setPreviewOpen(false);
      await loadDuplicates();
    } catch (e) { toast.error(apiError(e)); }
    finally { setMerging(false); }
  };

  const rollback = async (opId) => {
    if (!window.confirm("Annuler cette fusion ? Toutes les modifications seront restaurées.")) return;
    try {
      const r = await api.post(`/suppliers/dedup/rollback/${opId}`);
      toast.success(`${r.data.restored_sources} fournisseur(s) restauré(s)`);
      await loadOps();
    } catch (e) { toast.error(apiError(e)); }
  };

  const executeBulkRename = async () => {
    if (!bulkIds.length || (!bulkNewName.trim() && !bulkNewSiret.trim())) return;
    try {
      const r = await api.post("/suppliers/bulk-rename", {
        supplier_ids: bulkIds,
        new_name: bulkNewName.trim() || null,
        new_siret: bulkNewSiret.trim() || null,
      });
      toast.success(`${r.data.updated} fournisseur(s) mis à jour · ${r.data.invoices_updated} factures propagées`);
      setBulkOpen(false); setBulkIds([]); setBulkNewName(""); setBulkNewSiret("");
      await loadDuplicates();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-dedup-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>Nettoyage fournisseurs</h1>
          <p className="text-sm text-slate-500">Détection doublons · Fusion sécurisée · Rollback 30 jours</p>
        </div>
        <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-rename-btn">
          <Pencil className="w-4 h-4 mr-2" />Renommage massif
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="duplicates" data-testid="tab-duplicates"><Users className="w-4 h-4 mr-1" />Doublons</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-1" />Historique</TabsTrigger>
        </TabsList>

        <TabsContent value="duplicates" className="space-y-4 mt-4">
          {/* Fuzzy slider */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm">Sensibilité fuzzy match</Label>
                    <span className="font-mono text-sm font-medium">{fuzzy}%</span>
                  </div>
                  <Slider value={[fuzzy]} onValueChange={v => setFuzzy(v[0])} min={75} max={95} step={1} data-testid="fuzzy-slider" />
                  <p className="text-xs text-slate-500 mt-1">
                    75% = large (plus de suggestions) · 95% = strict (moins de faux positifs)
                  </p>
                </div>
                <Button onClick={loadDuplicates} disabled={loading} data-testid="scan-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                  Analyser
                </Button>
              </div>
            </CardContent>
          </Card>

          {scan && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Kpi label="Fournisseurs actifs" value={scan.total_suppliers} />
              <Kpi label="Groupes détectés" value={scan.group_count} tone={scan.group_count > 0 ? "amber" : ""} />
              <Kpi label="Fournisseurs concernés" value={scan.affected_supplier_count} tone={scan.affected_supplier_count > 0 ? "amber" : ""} />
              <Kpi label="Seuil fuzzy" value={`${scan.fuzzy_threshold}%`} />
            </div>
          )}

          {scan && scan.groups.length === 0 && (
            <Card><CardContent className="p-12 text-center text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
              <p className="font-medium">Aucun doublon détecté 🎉</p>
              <p className="text-sm mt-1">Ta base fournisseurs est propre à ce seuil.</p>
            </CardContent></Card>
          )}

          {scan?.groups.map((g, gi) => (
            <DupGroupCard
              key={g.group_id}
              group={g}
              idx={gi}
              expanded={!!expanded[g.group_id]}
              onToggle={() => setExpanded(p => ({ ...p, [g.group_id]: !p[g.group_id] }))}
              onMerge={openPreview}
            />
          ))}
        </TabsContent>

        <TabsContent value="history" className="space-y-2 mt-4" data-testid="ops-history">
          {ops.length === 0 && <p className="text-sm text-slate-400 text-center py-12">Aucune opération enregistrée</p>}
          {ops.map(op => (
            <Card key={op.id} data-testid={`op-${op.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={op.op_type === "merge" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}>
                      {op.op_type === "merge" ? "Fusion" : "Renommage"}
                    </Badge>
                    <span className="text-sm font-medium truncate">{op.target_name_after || "—"}</span>
                    {op.rolled_back_at && <Badge className="bg-slate-200 text-slate-700">Annulée le {fmtDate(op.rolled_back_at)}</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {op.source_ids.length} fournisseur(s) · {op.invoices_updated} facture(s) · {op.contracts_updated} contrat(s) · {fmtDate(op.created_at)} par {op.created_by_email}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {op.can_rollback ? `Rollback possible jusqu'au ${fmtDate(op.expires_at)}` : "Fenêtre de rollback expirée"}
                  </p>
                </div>
                {op.can_rollback && (
                  <Button variant="outline" size="sm" onClick={() => rollback(op.id)} data-testid={`rollback-${op.id}`}>
                    <RotateCcw className="w-3 h-3 mr-1" />Annuler
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Merge className="w-5 h-5" />Confirmer la fusion</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-xs uppercase text-emerald-700 font-medium">Cible conservée</p>
                <p className="font-semibold">{preview.target.name}</p>
                {preview.target.siret && <p className="text-xs text-slate-600 font-mono">SIRET {preview.target.siret}</p>}
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-xs uppercase text-slate-500 font-medium mb-1">{preview.sources.length} fournisseur(s) absorbé(s)</p>
                {preview.sources.map(s => (
                  <p key={s.id} className="text-sm">• {s.name} {s.siret && <span className="text-slate-400 font-mono text-xs">({s.siret})</span>}</p>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-medium mb-1">Impact</p>
                <p>{preview.impact.supplier_invoices} facture(s) fournisseur réassignée(s)</p>
                <p>{preview.impact.engagements_contrats} engagement(s)/contrat(s) réassigné(s)</p>
              </div>

              {preview.warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  {preview.warnings.map((w, i) => (
                    <p key={i} className="flex items-start gap-1"><AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />{w}</p>
                  ))}
                </div>
              )}

              <p className="text-xs text-slate-500">
                ⏱ Rollback possible pendant <b>30 jours</b> après validation.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Annuler</Button>
            <Button onClick={executeMerge} disabled={merging} data-testid="confirm-merge-btn">
              {merging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
              Confirmer la fusion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk rename dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-xl" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle>Renommage massif</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Sélectionne des fournisseurs et applique un nouveau nom + SIRET commun. Les factures liées sont mises à jour, le renommage est réversible 30 jours.
            </p>
            <div>
              <Label className="text-xs">IDs fournisseurs (un par ligne)</Label>
              <textarea
                id="bulk-ids-input"
                name="bulk-ids-input"
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono"
                rows={4}
                placeholder="Colle un ou plusieurs IDs..."
                value={bulkIds.join("\n")}
                onChange={e => setBulkIds(e.target.value.split("\n").map(s => s.trim()).filter(Boolean))}
                data-testid="bulk-ids-input"
              />
              <p className="text-xs text-slate-400 mt-1">{bulkIds.length} ID(s) — récupérables depuis la page Fournisseurs (bouton copier).</p>
            </div>
            <Input placeholder="Nouveau nom (optionnel)" value={bulkNewName} onChange={e => setBulkNewName(e.target.value)} data-testid="bulk-name-input" />
            <Input placeholder="Nouveau SIRET (optionnel)" value={bulkNewSiret} onChange={e => setBulkNewSiret(e.target.value)} data-testid="bulk-siret-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Annuler</Button>
            <Button onClick={executeBulkRename} disabled={!bulkIds.length || (!bulkNewName.trim() && !bulkNewSiret.trim())} data-testid="confirm-bulk-btn">
              Appliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white";
  return (
    <div className={`${cls} rounded-lg border p-4`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function DupGroupCard({ group, idx, expanded, onToggle, onMerge }) {
  const [target, setTarget] = useState(group.target_supplier_id);
  const [selected, setSelected] = useState(
    new Set(group.members.filter(m => !m.is_target).map(m => m.id))
  );

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const kind = KIND_LABEL[group.kind] || { label: group.kind, color: "bg-slate-100" };

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
      <Card data-testid={`dup-group-${group.group_id}`}>
        <CardContent className="p-0">
          <button onClick={onToggle} className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition text-left">
            {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={kind.color}>{kind.label}</Badge>
                <span className="font-semibold">{group.target_name}</span>
                {group.target_siret && <span className="text-xs text-slate-500 font-mono">SIRET {group.target_siret}</span>}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {group.member_count} entités · {group.total_invoices} facture(s) au total
              </p>
            </div>
            <Badge variant="outline" className="text-[11px]">{group.member_count} doublons</Badge>
          </button>

          {expanded && (
            <div className="border-t border-slate-100 p-4 space-y-3">
              <RadioGroup value={target} onValueChange={(v) => {
                setTarget(v);
                setSelected(new Set(group.members.filter(m => m.id !== v).map(m => m.id)));
              }}>
                {group.members.map(m => (
                  <div key={m.id} className={`flex items-center gap-3 p-2 rounded border ${target === m.id ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200"}`} data-testid={`member-row-${m.id}`}>
                    <RadioGroupItem value={m.id} id={`r-${m.id}`} data-testid={`radio-target-${m.id}`} />
                    <div className="flex-1 min-w-0">
                      <Label htmlFor={`r-${m.id}`} className="cursor-pointer text-sm font-medium">
                        {m.name}
                        {target === m.id && <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px]">Cible</Badge>}
                      </Label>
                      <div className="text-xs text-slate-500 flex gap-3">
                        {m.siret && <span className="font-mono">{m.siret}</span>}
                        {m.email && <span>{m.email}</span>}
                        <span>{m.invoice_count} facture(s)</span>
                        {m.fuzzy_score && <span className="text-amber-600">fuzzy {m.fuzzy_score}%</span>}
                      </div>
                    </div>
                    {target !== m.id && (
                      <label className="flex items-center gap-1 text-xs text-slate-600">
                        <input id={`chk-source-${m.id}`} name={`chk-source-${m.id}`}
                          type="checkbox" checked={selected.has(m.id)} onChange={() => toggleSelect(m.id)} data-testid={`chk-source-${m.id}`} />
                        Absorber
                      </label>
                    )}
                  </div>
                ))}
              </RadioGroup>

              <Button
                onClick={() => onMerge(group, target, Array.from(selected))}
                disabled={!target || selected.size === 0}
                data-testid={`merge-btn-${group.group_id}`}
              >
                <Merge className="w-4 h-4 mr-2" />
                Fusionner {selected.size} sur {group.members.find(m => m.id === target)?.name}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
